/**
 * Cultivation (แต้มฝึกปรือ) earned by being in the world: it builds up second by second and is paid
 * out every `CULTIVATION.tickSeconds` into the chat log. Meditating builds it faster, the group
 * dance lifts the rate (groupDance.ts), and a training spot underfoot adds its bonus beside the
 * dance's rather than multiplying it (trainingSpots.ts, data/trainingSpots.ts).
 */
import { CULTIVATION } from '../cultivation';
import type { TrainingSpotDef } from '../data/trainingSpots';
import { spotMult, spotOn, spotSuits, type TrainingSpotState } from '../trainingSpots';
import { TrainBoostLedger } from '../trainBoost';
import { autoMul } from './autoRules';
import type { SystemHost, WorldSystem } from './types';

export class CultivationSystem implements WorldSystem {
  readonly name = 'cultivation';
  /** Cultivation built up since the last payout, and seconds since it. */
  private acc = 0;
  private t = 0;
  /** The training spot the player stands in, and the one the next payout names. */
  private spot: TrainingSpotDef | null = null;
  private payoutSpot: string | null = null;
  /** Training-boost ledger (trainBoost.ts); `day` is the hook for the real-day reset. */
  readonly boost = new TrainBoostLedger();
  boostDay = 0;
  realDay(day: number): void {
    this.boostDay = day;
  }

  constructor(private readonly h: SystemHost) {}

  /** The training spot the player stands in, or null. */
  get trainingSpot(): TrainingSpotDef | null {
    return this.spot;
  }

  /**
   * Where the player trains now: the spot, its multiplier for them and whether it suits them, and
   * why the bonus is held back (it is a place for training, not for fighting or riding through).
   */
  spotState(): TrainingSpotState | null {
    const s = this.spot;
    if (!s) return null;
    const w = this.h.world;
    const p = w.player;
    const pr = w.progress;
    const paused = w.inArena()
      ? 'อยู่ในลานประลอง'
      : p.combatT > 0
        ? 'กำลังต่อสู้'
        : p.mount
          ? 'ลงจากพาหนะก่อน'
          : w.dance.me()
            ? 'กำลังรำกลุ่ม'
            : w.carriage.aboard().includes(p.id)
              ? 'อยู่บนรถม้า'
              : p.dead || p.jailT > 0
                ? 'ฝึกที่นี่ไม่ได้ตอนนี้'
                : null;
    return {
      spot: s,
      suits: spotSuits(s, pr, p.meditating),
      paused,
      mult: paused ? 1 : spotMult(s, pr, p.meditating),
    };
  }

  playerStep(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    this.syncSpot();
    const base = w.sys.qiPool.rate(p.meditating, dt);
    const here = this.spotState();
    const placeB = here && here.mult > 1 ? here.mult - 1 : 0;
    if (placeB > 0) this.payoutSpot = here!.spot.name;
    // A paying dance ring replaces the base rate; otherwise the dance buff is a ledger slot.
    const rate = w.dance.cultivationRate(base);
    const ring = rate !== base * w.dance.buffMult();
    this.boost.set('place', placeB);
    this.boost.set('dance', ring ? 0 : w.dance.buffMult() - 1);
    this.boost.tick(dt);
    this.acc += this.boost.gain(ring ? rate : base, dt, this.boostDay) * autoMul(w);
    if ((this.t += dt) >= CULTIVATION.tickSeconds) {
      this.t -= CULTIVATION.tickSeconds;
      const gain = Math.round(this.acc);
      this.acc = 0;
      if (gain > 0) {
        w.progress.cultivation += gain;
        const how = [p.meditating ? 'นั่งสมาธิ' : '', this.payoutSpot ?? ''].filter(Boolean).join(' · ');
        this.h.log('sys', `ฝึกปรือได้ ${gain} แต้ม${how ? ` (${how})` : ''}`);
      }
      this.payoutSpot = null;
    }
  }

  /** Keeps `spot` on the training spot underfoot (none indoors), announcing a step in or out. */
  private syncSpot(): void {
    const w = this.h.world;
    const p = w.player;
    const s = w.interiors.inside ? null : spotOn(w.map, p.x, p.y);
    if (s === this.spot) return;
    this.h.emit({ type: 'trainingSpot', id: s?.id ?? null, from: this.spot?.id ?? null });
    this.spot = s;
  }
}
